/**
 * 活动倒计时与宣传顶栏。
 * 维护边界：只管理当前 Section 实例的展示模式、ET 时间状态、计时器与会话关闭状态。
 */
(function () {
  const ELEMENT_NAME = 'bixdo-campaign-countdown';
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  class BixdoCampaignCountdown extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.isInitialized = true;
      this.isClosed = false;
      this.isDesignMode = this.dataset.designMode === 'true';
      this.displayMode = this.dataset.displayMode || 'countdown';
      this.mainView = this.querySelector('[data-main-view]');
      this.campaignView = this.querySelector('[data-campaign-view]');
      this.promotionView = this.querySelector('[data-promotion-view]');
      this.editorStatus = this.querySelector('[data-editor-status]');
      this.timer = this.querySelector('[data-countdown-timer]');
      this.stateElements = Array.from(this.querySelectorAll('[data-countdown-state]'));
      this.numberNodes = {
        days: this.querySelector('[data-countdown-days]'),
        hours: this.querySelector('[data-countdown-hours]'),
        minutes: this.querySelector('[data-countdown-minutes]'),
        seconds: this.querySelector('[data-countdown-seconds]')
      };
      this.closeButtons = Array.from(this.querySelectorAll('[data-countdown-close]'));
      this.handleClose = this.close.bind(this);
      this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));

      if (this.displayMode === 'promotion') {
        this.initializePromotion();
        return;
      }

      this.timeZone = this.dataset.timeZone || 'America/New_York';
      this.handleVisibilityChange = () => {
        if (!document.hidden) {
          this.tick();
        }
      };
      this.formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: this.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.initializeSchedule();
    }

    disconnectedCallback() {
      this.stopTimer();
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
      if (this.handleVisibilityChange) {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
      if (this.dataset.sticky === 'true') {
        document.documentElement.style.setProperty('--sticky-announcement-bar-enabled', '0');
        document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      }
      this.isInitialized = false;
    }

    initializePromotion() {
      this.dismissKey = [
        'bixdo-campaign-countdown',
        this.dataset.sectionId || 'section',
        'promotion',
        this.dataset.promotionKey || 'content'
      ].join(':');

      if (this.dataset.promotionHasContent !== 'true' || !this.promotionView) {
        this.showConfigurationError(
          '请至少填写宣传标签、宣传主文本或完整的 CTA 文本与链接。',
          '日常宣传'
        );
        return;
      }

      if (this.restoreDismissedState()) {
        return;
      }

      this.showPromotion('promotion');
    }

    initializeSchedule() {
      const previewStart = this.parseMoment(
        '预热开始',
        this.dataset.previewStartDate,
        this.dataset.previewStartTime,
        false
      );
      const campaignStart = this.parseMoment(
        '活动开始',
        this.dataset.campaignStartDate,
        this.dataset.campaignStartTime,
        true
      );
      const campaignEnd = this.parseMoment(
        '活动结束',
        this.dataset.campaignEndDate,
        this.dataset.campaignEndTime,
        true
      );

      const parseError = previewStart.error || campaignStart.error || campaignEnd.error;

      if (parseError) {
        this.showConfigurationError(parseError);
        return;
      }

      if (previewStart.value !== null && previewStart.value >= campaignStart.value) {
        this.showConfigurationError('预热开始必须早于活动开始，请检查日期和时间。');
        return;
      }

      if (campaignStart.value >= campaignEnd.value) {
        this.showConfigurationError('活动结束必须晚于活动开始，请检查日期和时间。');
        return;
      }

      this.schedule = {
        previewStart: previewStart.value,
        campaignStart: campaignStart.value,
        campaignEnd: campaignEnd.value
      };
      this.dismissKey = [
        'bixdo-campaign-countdown',
        this.dataset.sectionId || 'section',
        this.dataset.campaignStartDate,
        this.dataset.campaignStartTime,
        this.dataset.campaignEndDate,
        this.dataset.campaignEndTime
      ].join(':');

      if (this.restoreDismissedState()) {
        return;
      }

      this.tick();
    }

    parseMoment(label, dateValue = '', timeValue = '', required) {
      const date = dateValue.trim();
      const time = timeValue.trim();

      if (!date && !time && !required) {
        return { value: null, error: null };
      }

      if (!date || !time) {
        return { value: null, error: `${label}需同时填写日期和时间。` };
      }

      const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
      const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);

      if (!dateMatch || !timeMatch) {
        return { value: null, error: `${label}格式必须为 YYYY-MM-DD 和 HH:mm。` };
      }

      const components = {
        year: Number(dateMatch[1]),
        month: Number(dateMatch[2]),
        day: Number(dateMatch[3]),
        hour: Number(timeMatch[1]),
        minute: Number(timeMatch[2]),
        second: 0
      };

      if (!this.isValidCalendarTime(components)) {
        return { value: null, error: `${label}包含无效日期或时间，请检查输入。` };
      }

      const value = this.zonedTimeToEpoch(components);

      if (value === null) {
        return {
          value: null,
          error: `${label}处于美东夏令时切换的无效或重复时段，请避开 01:00–03:00。`
        };
      }

      return { value, error: null };
    }

    isValidCalendarTime(components) {
      if (components.hour > 23 || components.minute > 59) {
        return false;
      }

      const testDate = new Date(
        Date.UTC(
          components.year,
          components.month - 1,
          components.day,
          components.hour,
          components.minute,
          components.second
        )
      );

      return (
        testDate.getUTCFullYear() === components.year &&
        testDate.getUTCMonth() + 1 === components.month &&
        testDate.getUTCDate() === components.day
      );
    }

    zonedTimeToEpoch(components) {
      const utcGuess = Date.UTC(
        components.year,
        components.month - 1,
        components.day,
        components.hour,
        components.minute,
        components.second
      );
      // 切换日前后可能有两个不同偏移；只接受唯一对应的 ET 时刻。
      const offsets = new Set([
        this.getTimeZoneOffset(utcGuess - DAY),
        this.getTimeZoneOffset(utcGuess),
        this.getTimeZoneOffset(utcGuess + DAY)
      ]);
      const matches = Array.from(offsets)
        .map((offset) => utcGuess - offset)
        .filter((epoch) => this.sameParts(this.getZonedParts(epoch), components));

      return matches.length === 1 ? matches[0] : null;
    }

    getTimeZoneOffset(epoch) {
      const zoned = this.getZonedParts(epoch);
      const zonedAsUtc = Date.UTC(
        zoned.year,
        zoned.month - 1,
        zoned.day,
        zoned.hour,
        zoned.minute,
        zoned.second
      );
      const epochWithoutMilliseconds = Math.floor(epoch / SECOND) * SECOND;

      return zonedAsUtc - epochWithoutMilliseconds;
    }

    getZonedParts(epoch) {
      const parts = {};

      this.formatter.formatToParts(new Date(epoch)).forEach((part) => {
        if (part.type !== 'literal') {
          parts[part.type] = Number(part.value);
        }
      });

      return parts;
    }

    sameParts(left, right) {
      return (
        left.year === right.year &&
        left.month === right.month &&
        left.day === right.day &&
        left.hour === right.hour &&
        left.minute === right.minute &&
        left.second === right.second
      );
    }

    tick() {
      this.stopTimer();
      if (this.isClosed) {
        return;
      }
      const now = Date.now();

      if (this.schedule.previewStart !== null && now < this.schedule.previewStart) {
        this.showInactive('预热尚未开始；前台将按“非活动期行为”设置显示。');
        // 隐藏期间只做低频校时；到预热节点时会立即切换，不运行逐秒计时器。
        this.scheduleNext(Math.min(this.schedule.previewStart - now, MINUTE));
        return;
      }

      if (now < this.schedule.campaignStart) {
        this.showCampaign('preview', this.schedule.campaignStart, now);
        this.scheduleNext(Math.min(this.schedule.campaignStart - now, SECOND - (now % SECOND)));
        return;
      }

      if (now < this.schedule.campaignEnd) {
        this.showCampaign('live', this.schedule.campaignEnd, now);
        this.scheduleNext(Math.min(this.schedule.campaignEnd - now, SECOND - (now % SECOND)));
        return;
      }

      this.showInactive('活动已结束；前台将按“非活动期行为”设置显示。');
    }

    scheduleNext(delay) {
      this.timerId = window.setTimeout(() => this.tick(), Math.max(1, delay));
    }

    showCampaign(state, target, now) {
      this.hidden = false;
      if (this.mainView) {
        this.mainView.hidden = false;
      }

      if (this.dataset.state !== state) {
        this.dataset.state = state;
        this.stateElements.forEach((element) => {
          element.hidden = element.dataset.countdownState !== state;
        });
      }

      if (this.campaignView) {
        this.campaignView.hidden = false;
      }

      if (this.promotionView) {
        this.promotionView.hidden = true;
      }

      this.hideEditorStatus();
      this.updateTimer(target, now);
    }

    showPromotion(state) {
      this.stopTimer();
      this.dataset.state = state;

      if (this.campaignView) {
        this.campaignView.hidden = true;
      }

      if (!this.promotionView) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      if (this.mainView) {
        this.mainView.hidden = false;
      }
      this.promotionView.hidden = false;
      this.hideEditorStatus();
    }

    showInactive(editorMessage) {
      this.dataset.state = 'inactive';

      if (this.campaignView) {
        this.campaignView.hidden = true;
      }

      if (
        this.dataset.inactiveBehavior === 'fallback' &&
        this.dataset.promotionHasContent === 'true' &&
        this.promotionView
      ) {
        this.showPromotion('inactive');
        return;
      }

      if (this.promotionView) {
        this.promotionView.hidden = true;
      }

      if (this.isDesignMode && this.editorStatus) {
        this.hidden = false;
        if (this.mainView) {
          this.mainView.hidden = true;
        }
        this.editorStatus.textContent = editorMessage;
        this.editorStatus.hidden = false;
        return;
      }

      this.hidden = true;
    }

    showConfigurationError(message, context = '倒计时') {
      this.stopTimer();
      this.dataset.state = 'invalid';

      if (this.mainView) {
        this.mainView.hidden = true;
      }

      if (this.campaignView) {
        this.campaignView.hidden = true;
      }

      if (this.promotionView) {
        this.promotionView.hidden = true;
      }

      if (this.isDesignMode && this.editorStatus) {
        this.hidden = false;
        this.editorStatus.textContent = `${context}未展示：${message}`;
        this.editorStatus.hidden = false;
        return;
      }

      this.hidden = true;
    }

    hideEditorStatus() {
      if (this.editorStatus) {
        this.editorStatus.hidden = true;
        this.editorStatus.textContent = '';
      }
    }

    updateTimer(target, now) {
      const remaining = Math.max(0, target - now);
      const days = Math.floor(remaining / DAY);
      const hours = Math.floor((remaining % DAY) / HOUR);
      const minutes = Math.floor((remaining % HOUR) / MINUTE);
      const seconds = Math.floor((remaining % MINUTE) / SECOND);

      this.setNumber(this.numberNodes.days, days);
      this.setNumber(this.numberNodes.hours, hours);
      this.setNumber(this.numberNodes.minutes, minutes);
      this.setNumber(this.numberNodes.seconds, seconds);

      if (this.timer) {
        const accessibleTime = `${days} days, ${hours} hours, ${minutes} minutes remaining`;
        if (this.timer.getAttribute('aria-label') !== accessibleTime) {
          this.timer.setAttribute('aria-label', accessibleTime);
        }
      }
    }

    setNumber(element, value) {
      const nextValue = String(value).padStart(2, '0');
      if (element && element.textContent !== nextValue) {
        element.textContent = nextValue;
      }
    }

    close() {
      this.isClosed = true;
      if (this.dataset.dismissEnabled === 'true' && !this.isDesignMode) {
        try {
          window.sessionStorage.setItem(this.dismissKey, '1');
        } catch (error) {
          // Privacy settings can disable storage; closing the current instance still works.
        }
      }

      this.hidden = true;
      this.stopTimer();
    }

    isDismissed() {
      try {
        return window.sessionStorage.getItem(this.dismissKey) === '1';
      } catch (error) {
        return false;
      }
    }

    restoreDismissedState() {
      if (!this.isDesignMode && this.dataset.dismissEnabled === 'true' && this.isDismissed()) {
        this.isClosed = true;
        this.hidden = true;
        return true;
      }

      return false;
    }

    stopTimer() {
      if (this.timerId) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
    }
  }

  if (!window.customElements.get(ELEMENT_NAME)) {
    window.customElements.define(ELEMENT_NAME, BixdoCampaignCountdown);
  }
})();
